import { useState, useCallback } from 'react';
import type { AnalysisResult } from '../types';
import { findVideoSource, findAudioSource } from '../utils/sourceUtils';

type ChatMessage = { role: string; content: string };

function getStorageKey(result: AnalysisResult): string {
  if (result.timestamp) return `chat:${result.timestamp}`;
  if (result.fileUrls?.length) return `chat:${result.fileUrls[0]}`;
  return `chat:${result.category}:${result.processingTime ?? 'unknown'}`;
}

function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function loadSavedChat(result: AnalysisResult): ChatMessage[] | null {
  try {
    const primaryKey = getStorageKey(result);
    const hashKey = result.analysis ? `chat:hash:${hashString(result.analysis)}` : null;
    const tryKeys = [primaryKey, hashKey].filter(Boolean) as string[];

    for (const key of tryKeys) {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw) as ChatMessage[];
        console.debug('[useResultState] Loaded chat from key:', key);
        return parsed;
      } catch (parseError) {
        console.warn('[useResultState] Invalid JSON for key:', key);
        continue;
      }
    }

    // Fallback: scan all chat keys for matching transcript content
    const targetContent = result.analysis?.substring(0, 120) || '';
    if (!targetContent) return null;

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith('chat:')) continue;

      const raw = sessionStorage.getItem(key);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw) as ChatMessage[];
        const systemMsg = parsed.find((m) => m.role === 'system');

        if (systemMsg?.content?.includes(targetContent)) {
          console.debug('[useResultState] Loaded chat by scanning key:', key);
          return parsed;
        }
      } catch (parseError) {
        console.warn('[useResultState] Invalid JSON during scan, key:', key);
        continue;
      }
    }
    return null;
  } catch (error) {
    console.error('[useResultState] Unexpected error loading saved chat:', error);
    return null;
  }
}

export default function useResultState() {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
  const [showingResults, setShowingResults] = useState<boolean>(false);

  const handleSelectHistoryItem = useCallback((item: AnalysisResult) => {
    const savedChat = loadSavedChat(item);
    const augmentedResult = savedChat ? { ...item, chat: savedChat } : item;

    setResult(augmentedResult);
    setVideoObjectUrl(findVideoSource(item, null));
    setAudioObjectUrl(findAudioSource(item, null));
    setShowingResults(true);
  }, []);

  const setResultWithUrls = useCallback((item: AnalysisResult) => {
    const fileUrl = item.fileUrl || null;
    
    setResult(item);
    setVideoObjectUrl(findVideoSource(item, fileUrl));
    setAudioObjectUrl(findAudioSource(item, fileUrl));
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setVideoObjectUrl(null);
    setAudioObjectUrl(null);
    setShowingResults(false);
  }, []);

  return {
    result,
    videoObjectUrl,
    audioObjectUrl,
    showingResults,
    setShowingResults,
    handleSelectHistoryItem,
    setResultWithUrls,
    reset,
  } as const;
}
